<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soServiceCategoryName"
        v-model="formData.so_service_category_name"
        :label="t('Nome da Categoria de Serviço')"
        :placeholder="t('Digite o nome da categoria de serviço')"
        type="text"
        required
        :validation="validateName"
        data-testid="so-service-categories-name-input"
      />
      <small v-if="errors.so_service_category_name" class="error-message">{{ errors.so_service_category_name }}</small>
    </div>
    <div class="form-field">
      <input-text
        elem-id="soServiceCategoryIconSvg"
        v-model="formData.so_service_category_icon_svg"
        :label="t('Ícone SVG')"
        :placeholder="t('Cole o conteúdo SVG aqui')"
        data-testid="so-service-categories-icon-svg-input"
      />
      <div v-if="formData.so_service_category_icon_svg" class="svg-preview">
        <span class="svg-preview-label">{{ t('Preview') }}:</span>
        <span class="svg-preview-icon" v-html="sanitizeSvgIcon(formData.so_service_category_icon_svg)"></span>
      </div>
    </div>
    <!-- Category Attributes Picker (edit mode only) -->
    <SOCategoryAttributesPicker
      v-if="props.isEditMode && itemIdNumber"
      ref="attributesPickerRef"
      :categoryId="itemIdNumber"
      elemIdPrefix="soCategoryAttr"
    />
    <!-- Category Report Attributes Picker (edit mode only) -->
    <SOCategoryReportAttributesPicker
      v-if="props.isEditMode && itemIdNumber"
      ref="reportAttributesPickerRef"
      :categoryId="itemIdNumber"
      elemIdPrefix="soCategoryReportAttr"
    />
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeSvgIcon } from '../../utils/sanitizeSvgIcon';
import SOCategoryAttributesPicker from './SOCategoryAttributesPicker.vue';
import SOCategoryReportAttributesPicker from './SOCategoryReportAttributesPicker.vue';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface ServiceCategoryItem {
  so_service_category_name?: string;
  so_service_category_icon_svg?: string;
  [key: string]: unknown;
}

interface AttributesPickerInstance {
  saveAttributes: () => Promise<void>;
  loadAttributes?: () => Promise<void>;
}

interface FormError {
  message?: string;
  stack?: string;
  response?: { data?: { error?: string } };
}

interface Props {
  itemId?: Id | null;
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { getById, save } = useSOCatalog('service_categories');

// Convert itemId to number for consistency
const itemIdNumber = computed<number | null>(() => {
  if (!props.itemId) return null;
  const num = typeof props.itemId === 'string' ? parseInt(props.itemId, 10) : props.itemId;
  log('SOServiceCategoriesForm', 'itemIdNumber', 'Converted ID', { original: props.itemId, converted: num });
  return num;
});

// Reactive data
const formData = reactive<{
  so_service_category_name: string;
  so_service_category_icon_svg: string;
}>({
  so_service_category_name: '',
  so_service_category_icon_svg: ''
});

const errors = reactive<{ so_service_category_name: string }>({
  so_service_category_name: ''
});

const isLoading = ref<boolean>(false);
const attributesPickerRef = ref<AttributesPickerInstance | null>(null);
const reportAttributesPickerRef = ref<AttributesPickerInstance | null>(null);

// Validation function for InputText component
const validateName = (): boolean => {
  return !!formData.so_service_category_name && formData.so_service_category_name.trim() !== '' && formData.so_service_category_name.length <= 100;
};

const loadForEdit = async (): Promise<void> => {
  if (!itemIdNumber.value) {
    logWarn('SOServiceCategoriesForm', 'loadForEdit', 'No itemId provided');
    return;
  }

  log('SOServiceCategoriesForm', 'loadForEdit', 'Loading service category for edit', itemIdNumber.value);
  isLoading.value = true;

  try {
    const item = await getById(itemIdNumber.value) as ServiceCategoryItem | null | undefined;
    log('SOServiceCategoriesForm', 'loadForEdit', 'Loaded data', item);

    if (!item) {
      throw new Error('Dados da categoria de serviço não encontrados');
    }

    // Populate form
    formData.so_service_category_name = item.so_service_category_name || '';
    formData.so_service_category_icon_svg = item.so_service_category_icon_svg || '';

    // Clear errors
    clearErrors();
  } catch (error: unknown) {
    const err = error as FormError;
    logError('SOServiceCategoriesForm', 'loadForEdit', 'Error loading service category', {
      itemId: itemIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar categoria de serviço') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOServiceCategoriesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_service_category_name || formData.so_service_category_name.trim() === '') {
    errors.so_service_category_name = t('Campo obrigatório');
    isValid = false;
  } else if (formData.so_service_category_name.length > 100) {
    errors.so_service_category_name = t('Nome muito longo') + ' (max 100)';
    isValid = false;
  }

  log('SOServiceCategoriesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.so_service_category_name = '';
};

const resetForm = (): void => {
  log('SOServiceCategoriesForm', 'resetForm', 'Resetting form');
  formData.so_service_category_name = '';
  formData.so_service_category_icon_svg = '';
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('SOServiceCategoriesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOServiceCategoriesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload: Record<string, unknown> = {
      so_service_category_name: formData.so_service_category_name.trim(),
      so_service_category_icon_svg: formData.so_service_category_icon_svg || null
    };

    if (props.isEditMode && itemIdNumber.value) {
      // Update existing
      payload.so_service_category_id = itemIdNumber.value;
      log('SOServiceCategoriesForm', 'handleSubmit', 'Updating service category', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Categoria de serviço atualizada com sucesso'));
    } else {
      // Create new
      log('SOServiceCategoriesForm', 'handleSubmit', 'Creating service category', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Categoria de serviço criada com sucesso'));
    }

    // Save category attributes if in edit mode
    if (props.isEditMode && itemIdNumber.value && attributesPickerRef.value) {
      try {
        await attributesPickerRef.value.saveAttributes();
      } catch (e: unknown) {
        logError('SOServiceCategoriesForm', 'handleSubmit', 'item attributes save failed', e);
        const message = e instanceof Error ? e.message : String(e);
        eventBus.emit('globalMessage.error', t('Erro ao salvar atributos de item') + ': ' + message);
      }
    }
    if (props.isEditMode && itemIdNumber.value && reportAttributesPickerRef.value) {
      try {
        await reportAttributesPickerRef.value.saveAttributes();
      } catch (e: unknown) {
        logError('SOServiceCategoriesForm', 'handleSubmit', 'report attributes save failed', e);
        const message = e instanceof Error ? e.message : String(e);
        eventBus.emit('globalMessage.error', t('Erro ao salvar atributos de laudo') + ': ' + message);
      }
    }

    emit('submit-success');
    resetForm();
  } catch (error: unknown) {
    logError('SOServiceCategoriesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar categoria de serviço') + ': ' + message
        : t('Erro ao criar categoria de serviço') + ': ' + message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in itemId to reload data
watch(() => props.itemId, async (newId) => {
  log('SOServiceCategoriesForm', 'watch', 'itemId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(() => {
  log('SOServiceCategoriesForm', 'mounted', 'Component mounted', {
    itemId: props.itemId,
    itemIdNumber: itemIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && itemIdNumber.value) {
    loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.svg-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: var(--p-surface-50);
  border-radius: 6px;
}

.svg-preview-label {
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.svg-preview-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.svg-preview-icon :deep(svg) {
  width: 100%;
  height: 100%;
}
</style>
